import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReferredByFilter, { referredByFromSearch } from '../components/contacts/ReferredByFilter';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import ReassignOwnerDialog from '../components/contacts/ReassignOwnerDialog';
import OwnerRequestsDialog, { UndoOwnerChangeDialog } from '../components/contacts/OwnerRequestsDialog';
import { createOwnerOperationsApi, useBackgroundReload } from '../components/contacts/ownerOperationsApi';
import axios from 'axios';
import { toast } from 'sonner';
import { bulkTagMessage } from '../lib/bulkTagMessage';
import { Users, Plus, Search, Loader2, Trash2, AlertTriangle, RotateCcw, Tag, Send, UserCog, ClipboardCheck } from 'lucide-react';
import { CalendarDays } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TagInput from '../components/TagInput';
import ContactModal, { SENIORITY } from '../components/contacts/ContactModal';
import DataTable from '../components/table/DataTable';
import { TablePagination, TableRangeBar } from '../components/table/SortableHeader';
import ViewSelector from '../components/table/ViewSelector';
import ColumnPicker from '../components/table/ColumnPicker';
import { CONTACT_FIELD_REGISTRY, DEFAULT_CONTACT_COLUMNS, buildVisibleColumns, getAllFields, isSortableColumn } from '../components/table/columnRegistry';
import useTableView from '../hooks/useTableView';
import useServerPaging, { PAGE_SIZE_OPTIONS } from '../hooks/useServerPaging';
import useCustomFields from '../hooks/useCustomFields';
import { errorText } from '../lib/apiErrors';
import { teammateLabels } from '../lib/teammateLabels';
import { ownerStepSkipWarning } from '../lib/enrollWarnings';
import { createEventContactsApi } from '../lib/eventContactsApi';
import { eventEnrollmentOutcomeMessages } from '../lib/eventEnrollmentOutcomes';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// #1000: the page size is remembered per list; the Deleted tab shares it.
const PAGE_SIZE_KEY = 'nurture.list.contacts.pageSize';
const SEARCH_DEBOUNCE_MS = 300;

// #979 P7: the bell's owner rows link to /contacts?owner_id=<user_id>. The deep
// link seeds the owner filter; a non-empty string only — anything else is
// ignored and the page keeps its default filter.
export function ownerFromSearch(search) {
    try {
        const value = new URLSearchParams(search || '').get('owner_id');
        return typeof value === 'string' && value.trim() ? value : '';
    } catch {
        return '';
    }
}


export default function Contacts() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // #863: the Contact Owner controls gate on EFFECTIVE permissions only, which
    // fail closed while unknown — never on can(), whose legacy union over-shows
    // them, nor on is_admin (managers may reassign, D2).
    const { hasEffectivePermission, contactScope } = usePermissions();
    const assignedOnly = contactScope === 'assigned';
    const canReassignOwner = hasEffectivePermission('contacts:assign_owner');
    const canApproveOwnerChanges = hasEffectivePermission('contacts:override_owner');
    const canRestoreOwner = hasEffectivePermission('contacts:restore_owner');
    // #793 S6: Profile's "View in Contacts" arrives with ?referred_by=me. Credited
    // leads can be owned by someone else (an existing owner is never replaced), so
    // that entry point starts from All Contact Owners rather than the usual "mine".
    const initialReferrer = referredByFromSearch(location?.search);
    // #979 P7: the bell's owner rows link to /contacts?owner_id=<user_id> — the
    // deep link seeds the owner filter, ignored for assigned-only users (the
    // response is server-scoped; a client owner filter would hide rows).
    const initialOwner = ownerFromSearch(location?.search);

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    // #1000: the input updates at once; the list reloads after a pause in typing.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);
    const paging = useServerPaging(PAGE_SIZE_KEY);
    const deletedPaging = useServerPaging(PAGE_SIZE_KEY);
    const [filterOwner, setFilterOwner] = useState(
        !assignedOnly && initialOwner ? initialOwner : (initialReferrer ? '' : (user?.user_id || ''))); // Default to current user
    const [filterReferrer, setFilterReferrer] = useState(initialReferrer);
    // An assigned-only response is server-scoped; never add a client owner filter,
    // which would hide contacts coached by the current user.
    useEffect(() => {
        if (assignedOnly) setFilterOwner('');
        // Initialize the default once, but do not treat an explicit empty
        // selection as uninitialized. Empty means "All Contact Owners".
        else if (!initialReferrer && user?.user_id) {
            setFilterOwner(current => current || user.user_id);
        }
    }, [assignedOnly, initialReferrer, user?.user_id]);
    // #799 QA: a credited lead can be owned by anyone (an owner is never replaced
    // by a credit), so choosing a referrer clears the owner filter — as the
    // Profile deep link already does — instead of silently showing only the
    // credited leads you happen to own. The owner can be narrowed again after.
    const changeReferrer = (value) => {
        setFilterReferrer(value);
        if (value) setFilterOwner('');
    };
    // #799 QA: keep `?referred_by=` in step with the filter, so a reload or a
    // copied link shows what is on screen (it used to bring "me" back).
    useEffect(() => {
        const params = new URLSearchParams(location?.search || '');
        if ((params.get('referred_by') || '') === filterReferrer) return;
        if (filterReferrer) params.set('referred_by', filterReferrer);
        else params.delete('referred_by');
        const query = params.toString();
        navigate({ pathname: location?.pathname || '/contacts', search: query ? `?${query}` : '' }, { replace: true });
    }, [filterReferrer]); // eslint-disable-line react-hooks/exhaustive-deps
    const [filterSeniority, setFilterSeniority] = useState('');
    // v2.16.39 BUG-039: tag filter on the Contacts toolbar. The grid
    // already paginated/filtered at the backend level via ?tag=.
    const [filterTag, setFilterTag] = useState('');
    const [tagOptions, setTagOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [total, setTotal] = useState(0);
    const [teamMembers, setTeamMembers] = useState([]);

    // Selection & bulk delete
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Deleted tab
    const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' | 'deleted'
    const [deletedContacts, setDeletedContacts] = useState([]);
    const [deletedTotal, setDeletedTotal] = useState(0);
    const [restoring, setRestoring] = useState(false);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [bulkTagAction, setBulkTagAction] = useState('add');
    const [bulkTags, setBulkTags] = useState([]);
    const [bulkTagging, setBulkTagging] = useState(false);
    // BUG-040 — bulk-enroll into a campaign
    const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
    const [campaignsList, setCampaignsList] = useState([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [enrolling, setEnrolling] = useState(false);
    const [showEventEnrollModal, setShowEventEnrollModal] = useState(false);
    const [eventOptions, setEventOptions] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [eventEnrolling, setEventEnrolling] = useState(false);
    const eventsApi = useMemo(() => createEventContactsApi(getAuthHeader), [getAuthHeader]);

    // #863 — Contact Owner reassignment, the requests dialog and Undo
    const [showReassign, setShowReassign] = useState(false);
    const [showOwnerRequests, setShowOwnerRequests] = useState(false);
    const [ownerRequestsTotal, setOwnerRequestsTotal] = useState(0);
    const [undoOperationId, setUndoOperationId] = useState(null);
    const ownerOps = useMemo(() => createOwnerOperationsApi(getAuthHeader), [getAuthHeader]);
    // #864: teammates who share a name are told apart ("Name (email)").
    const ownerLabels = useMemo(() => teammateLabels(teamMembers), [teamMembers]);

    // Table view management
    const {
        views, activeViewId, visibleColumnKeys, columnSizing, sorting, isDirty,
        switchView, saveView, saveAsView, deleteView, pushToOrg, addColumn,
        removeColumn, setSorting, setColumnSizing,
    } = useTableView('contact', DEFAULT_CONTACT_COLUMNS);

    // Custom fields
    const { fields: customFields } = useCustomFields('contact');

    // Build visible column configs
    const visibleColumns = buildVisibleColumns(CONTACT_FIELD_REGISTRY,
        visibleColumnKeys.map((key, idx) => ({ field_key: key, visible: true, order: idx, width: columnSizing[key] })),
        customFields
    );
    const allFields = getAllFields(CONTACT_FIELD_REGISTRY, customFields);
    const visibleKeySet = new Set(visibleColumnKeys);

    // #1000: the server sorts every row. A saved view sorted by a column it cannot
    // sort (Company) is not sent, and no header claims that sort.
    const sortId = sorting[0] && isSortableColumn(sorting[0].id, CONTACT_FIELD_REGISTRY) ? sorting[0].id : null;
    const sortDesc = Boolean(sortId && sorting[0].desc);
    const serverSorting = useMemo(() => (sortId ? [{ id: sortId, desc: sortDesc }] : []), [sortId, sortDesc]);

    // Load team members for owner filter
    useEffect(() => {
        const loadTeam = async () => {
            try {
                const res = await axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() });
                setTeamMembers(res.data || []);
            } catch { /* ignore */ }
        };
        loadTeam();
    }, [getAuthHeader]);

    // v2.16.39 BUG-039: populate the tag-filter dropdown from the tracker's
    // contact-scoped tag aggregation.
    useEffect(() => {
        const loadTags = async () => {
            try {
                const res = await axios.get(`${API}/tags?entity=contact`, { headers: getAuthHeader() });
                setTagOptions(Array.isArray(res.data) ? res.data : []);
            } catch { /* ignore */ }
        };
        loadTags();
    }, [getAuthHeader]);


    // Load one page of contacts with filters (#1000: the server pages and sorts).
    const { page, pageSize, clampAfterLoad, resetPage } = paging;
    const latestRequest = useRef(0);
    const loadContacts = useCallback(async (retryCount = 0) => {
        const request = ++latestRequest.current;
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterOwner && !assignedOnly) params.set('owner_id', filterOwner);
            if (filterSeniority) params.set('seniority', filterSeniority);
            if (filterTag) params.set('tag', filterTag);
            if (filterReferrer) params.set('referred_by', filterReferrer);
            if (sortId) {
                params.set('sort_by', sortId);
                params.set('sort_direction', sortDesc ? 'desc' : 'asc');
            }
            params.set('page', String(page));
            params.set('page_size', String(pageSize));

            const res = await axios.get(`${API}/contacts?${params}`, { headers: getAuthHeader() });
            if (request !== latestRequest.current) return; // a newer page or filter won
            const rows = Array.isArray(res.data) ? res.data : (res.data.data || []);
            const count = Array.isArray(res.data) ? res.data.length : (res.data.total || 0);
            setContacts(rows);
            setTotal(count);
            clampAfterLoad(rows.length, count);
        } catch (err) {
            if (request !== latestRequest.current) return; // a newer page or filter won; never retry the old one
            if (retryCount < 1 && (!err?.response || err?.message === 'Network Error')) {
                setTimeout(() => loadContacts(retryCount + 1), 1000);
                return;
            }
        }
        finally { setLoading(false); }
    }, [search, filterOwner, filterSeniority, filterTag, filterReferrer, assignedOnly, sortId, sortDesc, page, pageSize, clampAfterLoad, getAuthHeader]);

    // #1000: a new search, filter or sort starts again at page 1 — in the same pass, so the
    // old page is never fetched first (one request per change, not two).
    const listKey = JSON.stringify([search, filterOwner, filterSeniority, filterTag, filterReferrer, assignedOnly, sortId, sortDesc]);
    const loadedKey = useRef(null);
    useEffect(() => {
        const changed = loadedKey.current !== null && loadedKey.current !== listKey;
        loadedKey.current = listKey;
        if (changed && page !== 1) { resetPage(); return; }
        loadContacts();
    }, [loadContacts, listKey, page, resetPage]);

    // #863: a selection belongs to the list it was made in. Any search, filter,
    // sort (#1000) or tab change clears it, so a bulk action never reaches
    // contacts that are no longer in the list. Moving between pages keeps it
    // (#1000): the bulk actions send ids, so a pick on another page still counts.
    useEffect(() => { setSelectedIds(new Set()); }, [search, filterOwner, filterSeniority, filterTag, filterReferrer, sortId, sortDesc, activeTab]);

    // #1000: the rows behind the selection, kept as they are ticked, so names
    // (reassign preview, event outcomes) resolve for picks on other pages.
    const selectedRows = useRef(new Map());
    useEffect(() => {
        const rows = selectedRows.current;
        [...rows.keys()].forEach(id => { if (!selectedIds.has(id)) rows.delete(id); });
        [...contacts, ...deletedContacts].forEach(c => { if (selectedIds.has(c.contact_id)) rows.set(c.contact_id, c); });
    }, [contacts, deletedContacts, selectedIds]);
    const pageRows = activeTab === 'deleted' ? deletedContacts : contacts;
    const offPageSelected = [...selectedIds].filter(id => !pageRows.some(c => c.contact_id === id)).length;

    // #863: the approvers' badge, and the bell link `?ownerRequests=open`.
    const loadOwnerRequestsTotal = useCallback(async () => {
        if (!canApproveOwnerChanges) return;
        try {
            const res = await ownerOps.list('pending_approval', { limit: 1 });
            setOwnerRequestsTotal(res?.total || 0);
        } catch { /* the badge is only a hint */ }
    }, [canApproveOwnerChanges, ownerOps]);
    useEffect(() => { loadOwnerRequestsTotal(); }, [loadOwnerRequestsTotal]);
    useEffect(() => {
        if (new URLSearchParams(location?.search || '').get('ownerRequests') === 'open') setShowOwnerRequests(true);
    }, [location?.search]);
    const closeOwnerRequests = () => {
        setShowOwnerRequests(false);
        const params = new URLSearchParams(location?.search || '');
        if (!params.has('ownerRequests')) return;
        params.delete('ownerRequests');
        const query = params.toString();
        navigate({ pathname: location?.pathname || '/contacts', search: query ? `?${query}` : '' }, { replace: true });
    };
    const contactLabel = (id) => {
        const c = selectedRows.current.get(id) || contacts.find((x) => x.contact_id === id);
        return c ? (`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || id) : id;
    };
    // Every row changed: clear the selection. Some failed: select exactly those,
    // so one click re-previews them against their current Contact Owner.
    // Closed into the background there is no outcome yet: clear, reload now and later.
    const reloadContacts = useBackgroundReload(loadContacts);
    const handleReassignDone = (res, { background } = {}) => {
        if (background) { setSelectedIds(new Set()); reloadContacts({ background }); return; }
        if (!res) return;
        const failed = (res.results || []).filter((r) => !r.ok).map((r) => r.row_id);
        setSelectedIds(new Set(res.failed > 0 ? failed : []));
        loadContacts();
        loadOwnerRequestsTotal();
    };

    const handleSave = (saved) => {
        setContacts(prev => {
            const idx = prev.findIndex(c => c.contact_id === saved.contact_id);
            if (idx >= 0) { const u = [...prev]; u[idx] = saved; return u; }
            return [saved, ...prev];
        });
        // Reload to get enriched data (account_name, owner_name)
        loadContacts();
    };

    // Load one page of deleted contacts (#1000)
    const { page: deletedPage, pageSize: deletedPageSize, clampAfterLoad: clampDeleted } = deletedPaging;
    const loadDeletedContacts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/contacts/deleted`, {
                params: { page: deletedPage, page_size: deletedPageSize }, headers: getAuthHeader(),
            });
            const rows = res.data.data || [];
            setDeletedContacts(rows);
            setDeletedTotal(res.data.total || 0);
            clampDeleted(rows.length, res.data.total || 0);
        } catch { /* ignore - user may not have permission */ }
    }, [deletedPage, deletedPageSize, clampDeleted, getAuthHeader]);

    useEffect(() => {
        if (activeTab === 'deleted') loadDeletedContacts();
    }, [activeTab, loadDeletedContacts]);

    const handleBulkDelete = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            await axios.delete(`${API}/contacts/bulk`, { data: { contact_ids: ids }, headers: getAuthHeader() });
            toast.success(`${ids.length} contact${ids.length > 1 ? 's' : ''} deleted`);
            setSelectedIds(new Set());
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            loadContacts();
        } catch (err) { toast.error(errorText(err, 'Failed to delete contacts')); }
        finally { setDeleting(false); }
    };

    const handleRestore = async (ids) => {
        const restoreIds = ids || Array.from(selectedIds);
        if (!restoreIds.length) return;
        setRestoring(true);
        try {
            const res = await axios.put(`${API}/contacts/restore`, { contact_ids: restoreIds }, { headers: getAuthHeader() });
            const count = res?.data?.count ?? restoreIds.length;
            toast.success(`${count} contact${count === 1 ? '' : 's'} restored`);
            // #795: a contact whose email was taken while it was being restored
            // stays deleted; say so rather than counting it.
            const left = res?.data?.not_restored?.length || 0;
            if (left) toast.error(`${left} contact${left === 1 ? '' : 's'} not restored: another contact now uses the same email.`);
            setSelectedIds(new Set());
            loadDeletedContacts();
            loadContacts();
        } catch (err) {
            // #795: 409 when another contact already uses the email; the server
            // says how many, and nothing in the selection was restored.
            toast.error(errorText(err, 'Failed to restore contacts'));
        }
        finally { setRestoring(false); }
    };

    // BUG-040 — load active + draft campaigns for the bulk-enroll picker
    const loadEventsForEnroll = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/events`, { params: { status: 'published', page_size: 100 }, headers: getAuthHeader() });
            const rows = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setEventOptions(rows);
        } catch (err) {
            toast.error(errorText(err, 'Failed to load events'));
        }
    }, [getAuthHeader]);

    const handleEventEnroll = async () => {
        if (!selectedEventId || selectedIds.size === 0) return;
        setEventEnrolling(true);
        try {
            const selectedContacts = [...selectedIds].map(id => selectedRows.current.get(id)).filter(Boolean);
            const result = await eventsApi.addAttendees(selectedEventId, {
                contact_ids: [...selectedIds], outreach: true, operation_id: `contacts-enroll:${Date.now()}`, include_outcomes: true,
            });
            if (result.outcomes?.length) {
                eventEnrollmentOutcomeMessages(result.outcomes, selectedContacts)
                    .forEach(({ tone, message }) => toast[tone](message));
            } else if (result.created || result.restored) {
                toast.success(`${(result.created || 0) + (result.restored || 0)} contact(s) enrolled`);
            }
            setShowEventEnrollModal(false);
            setSelectedIds(new Set());
        } catch (err) {
            toast.error(errorText(err, 'Failed to enroll contacts in event'));
        } finally {
            setEventEnrolling(false);
        }
    };


    const loadCampaignsForEnroll = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
            const items = Array.isArray(res.data) ? res.data : (res.data.data || res.data.campaigns || []);
            setCampaignsList(items);
        } catch (err) {
            console.error('Failed to load campaigns:', err);
            toast.error('Failed to load campaigns');
        }
    }, [getAuthHeader]);

    const handleBulkEnroll = async () => {
        if (!selectedCampaignId) { toast.error('Please select a campaign'); return; }
        setEnrolling(true);
        try {
            const res = await axios.post(`${API}/campaigns/${selectedCampaignId}/bulk-enroll`, {
                contact_ids: Array.from(selectedIds),
            }, { headers: getAuthHeader() });
            const { enrolled = 0, skipped = 0, failed = 0 } = res.data || {};
            if (failed > 0) {
                toast.error(`${enrolled} enrolled, ${skipped} skipped, ${failed} failed — see server logs`, { duration: 8000 });
            } else {
                toast.success(`${enrolled} contact${enrolled !== 1 ? 's' : ''} enrolled${skipped ? `, ${skipped} skipped (already enrolled or invalid)` : ''}`);
            }
            // #864 (D9, A17): the campaign's owner-reassignment steps will skip for this caller.
            const ownerWarning = ownerStepSkipWarning(res.data);
            if (ownerWarning) toast.warning(ownerWarning, { duration: 8000 });
            setShowBulkEnrollModal(false);
            setSelectedCampaignId('');
            setSelectedIds(new Set());
        } catch (err) {
            toast.error(errorText(err, 'Failed to enroll contacts'), { duration: 8000 });
            console.error('Bulk-enroll failed:', err);
        }
        finally { setEnrolling(false); }
    };

    const handleBulkTag = async () => {
        if (!bulkTags.length) { toast.error('Please add at least one tag'); return; }
        setBulkTagging(true);
        try {
            const res = await axios.post(`${API}/contacts/bulk-tag`, {
                contact_ids: Array.from(selectedIds),
                action: bulkTagAction,
                tags: bulkTags,
            }, { headers: getAuthHeader() });
            const { tone, message } = bulkTagMessage({ action: bulkTagAction, count: res.data.count, changed: res.data.changed, noun: 'contacts' });
            toast[tone](message);
            setShowBulkTagModal(false);
            setBulkTags([]);
            setSelectedIds(new Set());
            loadContacts();
        } catch (err) {
            // BUG-041 — old empty catch hid 403/permission errors as "silently
            // fails" (Joseph's report). Surface the server detail so users can
            // see whether it's a permission issue, validation, or something else.
            // errorText (#863): a dict or array `detail` rendered as [object Object].
            toast.error(errorText(err, 'Failed to update tags'), { duration: 8000 });
            console.error('Bulk-tag failed:', err);
        }
        finally { setBulkTagging(false); }
    };

    const isAdmin = user?.is_admin;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Contacts</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <Users className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm text-stone-500" data-field="contact.count">
                            {activeTab === 'deleted' ? `${deletedTotal} deleted` : `${total} contacts`}
                            {assignedOnly && activeTab === 'contacts' ? ' · Contacts you own or coach' : ''}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Bulk action buttons */}
                    {selectedIds.size > 0 && offPageSelected > 0 && (
                        <span className="text-xs text-stone-500 tabular-nums whitespace-nowrap" data-testid="selection-off-page">
                            {selectedIds.size} selected · {offPageSelected} on other pages
                        </span>
                    )}
                    {selectedIds.size > 0 && activeTab === 'contacts' && (
                        <>
                            <button onClick={() => { setBulkTags([]); setBulkTagAction('add'); setShowBulkTagModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                                <Tag className="w-4 h-4" /> Tag ({selectedIds.size})
                            </button>
                            <button onClick={() => { setSelectedCampaignId(''); loadCampaignsForEnroll(); setShowBulkEnrollModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                                <Send className="w-4 h-4" /> Enroll ({selectedIds.size})
                            </button>
                            <button onClick={() => { setSelectedEventId(''); loadEventsForEnroll(); setShowEventEnrollModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors"
                                data-testid="enroll-event-btn">
                                <CalendarDays className="w-4 h-4" /> Add to event ({selectedIds.size})
                            </button>
                            {canReassignOwner && (
                                <button onClick={() => setShowReassign(true)} data-testid="reassign-owner-btn"
                                    className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                                    <UserCog className="w-4 h-4" /> Reassign Contact Owner ({selectedIds.size})
                                </button>
                            )}
                            <button onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                                <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
                            </button>
                        </>
                    )}
                    {selectedIds.size > 0 && activeTab === 'deleted' && (
                        <button onClick={() => handleRestore()} disabled={restoring}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            <RotateCcw className="w-4 h-4" /> Restore ({selectedIds.size})
                        </button>
                    )}
                    {(canApproveOwnerChanges || canRestoreOwner) && (
                        <button onClick={() => setShowOwnerRequests(true)} data-testid="owner-requests-btn"
                            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-stone-600 hover:text-stone-800 hover:bg-stone-100 transition-colors">
                            <ClipboardCheck className="w-3.5 h-3.5" /> Contact Owner requests
                            {canApproveOwnerChanges && ownerRequestsTotal > 0 && (
                                <span className="ml-1 px-1.5 rounded-full bg-amber-100 text-amber-800 text-xs tabular-nums" data-testid="owner-requests-badge">{ownerRequestsTotal}</span>
                            )}
                        </button>
                    )}
                    {/* Tab buttons */}
                    {isAdmin && (
                        <button onClick={() => { setActiveTab(activeTab === 'deleted' ? 'contacts' : 'deleted'); setSelectedIds(new Set()); }}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === 'deleted' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
                            }`}>
                            <Trash2 className="w-3.5 h-3.5" /> Deleted
                        </button>
                    )}
                    <button onClick={() => { setEditContact(null); setShowModal(true); }} data-testid="new-contact-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors">
                        <Plus className="w-4 h-4" /> New Contact
                    </button>
                </div>
            </div>

            {/* Toolbar: View Selector + Filters + Columns */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <ViewSelector
                        views={views}
                        activeViewId={activeViewId}
                        isDirty={isDirty}
                        onSwitchView={switchView}
                        onSaveView={saveView}
                        onSaveAsView={saveAsView}
                        onDeleteView={deleteView}
                        onPushToOrg={pushToOrg}
                        isAdmin={isAdmin}
                    />
                    <div className="w-px h-6 bg-stone-200" />
                    <div className="relative flex-1 min-w-[200px] max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search contacts..." value={searchInput} onChange={e => setSearchInput(e.target.value)}
                            data-testid="contacts-search-input"
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)} disabled={assignedOnly}
                        title={assignedOnly ? 'The server shows contacts you own or coach' : undefined}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Contact Owners</option>
                        {/* #864: reserved filters (the backend's owner_id=__none__ / __former__) */}
                        <option value="__none__">No Contact Owner</option>
                        <option value="__former__">Former member</option>
                        {teamMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>{ownerLabels.get(m.user_id) || m.name}</option>
                        ))}
                    </select>
                    <select value={filterSeniority} onChange={e => setFilterSeniority(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                        <option value="">All Seniority</option>
                        {SENIORITY.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {/* v2.16.39 BUG-039: tag filter */}
                    <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none"
                        title="Filter contacts by tag">
                        <option value="">All Tags</option>
                        {tagOptions.map(t => (
                            <option key={t.tag} value={t.tag}>
                                {t.tag}{typeof t.count === 'number' ? ` (${t.count})` : ''}
                            </option>
                        ))}
                    </select>
                    <ReferredByFilter value={filterReferrer} onChange={changeReferrer} teamMembers={teamMembers} currentUserId={user?.user_id} />
                </div>
                <ColumnPicker
                    allFields={allFields}
                    visibleKeys={visibleKeySet}
                    lockedKey="full_name"
                    onToggle={(key, on) => (on ? addColumn(key) : removeColumn(key))}
                />
            </div>

            {/* Data Table */}
            {activeTab === 'contacts' ? (
                loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                ) : (
                    <DataTable
                        data={contacts}
                        visibleColumns={visibleColumns}
                        sorting={serverSorting}
                        onSortingChange={setSorting}
                        manualSorting
                        header={(
                            <TableRangeBar page={page} pageSize={pageSize} total={total} noun="contacts"
                                onPageSizeChange={paging.setPageSize} pageSizeOptions={PAGE_SIZE_OPTIONS} />
                        )}
                        footer={(
                            <TablePagination variant="split" alwaysShow page={page} totalPages={paging.totalPages(total)}
                                total={total} pageSize={pageSize} onPageChange={paging.setPage}
                                onPageSizeChange={paging.setPageSize} />
                        )}
                        columnSizing={columnSizing}
                        onColumnSizingChange={setColumnSizing}
                        onRowClick={(row) => navigate(`/contacts/${row.contact_id}`)}
                        selectable
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        idField="contact_id"
                    />
                )
            ) : (
                /* Deleted contacts table */
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <TableRangeBar page={deletedPage} pageSize={deletedPageSize} total={deletedTotal} noun="deleted contacts"
                        onPageSizeChange={deletedPaging.setPageSize} pageSizeOptions={PAGE_SIZE_OPTIONS} />
                    <div className="overflow-auto">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="px-3 py-2.5 text-left w-10">
                                    <input type="checkbox" aria-label="Select all rows on this page"
                                        checked={deletedContacts.length > 0 && deletedContacts.every(c => selectedIds.has(c.contact_id))}
                                        ref={el => { if (el) el.indeterminate = deletedContacts.some(c => selectedIds.has(c.contact_id)) && !deletedContacts.every(c => selectedIds.has(c.contact_id)); }}
                                        onChange={(e) => {
                                            // #1000: only this page's rows change; other pages' picks stay.
                                            const next = new Set(selectedIds);
                                            deletedContacts.forEach(c => (e.target.checked ? next.add(c.contact_id) : next.delete(c.contact_id)));
                                            setSelectedIds(next);
                                        }}
                                        className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                </th>
                                {['Name', 'Email', 'Title', 'Deleted By', 'Deleted At', ''].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deletedContacts.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-16 text-sm text-stone-400">No deleted contacts</td></tr>
                            ) : deletedContacts.map(c => (
                                <tr key={c.contact_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-3 py-2.5">
                                        <input type="checkbox" checked={selectedIds.has(c.contact_id)}
                                            onChange={(e) => {
                                                const next = new Set(selectedIds);
                                                if (e.target.checked) next.add(c.contact_id);
                                                else next.delete(c.contact_id);
                                                setSelectedIds(next);
                                            }}
                                            className="w-4 h-4 rounded border-stone-300 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-2.5 text-sm font-medium text-stone-900">
                                        {/* A deleted contact opens read-only, with its history (#910). */}
                                        <button type="button" className="text-left hover:underline" onClick={() => navigate(`/contacts/${encodeURIComponent(c.contact_id)}`)} data-testid={`deleted-contact-link-${c.contact_id}`}>
                                            {c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.contact_id}
                                        </button>
                                        {c.merged_into_contact_id && (
                                            <div className="text-xs font-normal text-stone-500" data-testid={`deleted-contact-merged-${c.contact_id}`}>
                                                Merged into{' '}
                                                <button type="button" className="underline underline-offset-2" onClick={() => navigate(`/contacts/${encodeURIComponent(c.merged_into_contact_id)}`)}>{c.merged_into_name || 'the surviving contact'}</button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.email || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-600">{c.title || '—'}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{c.deleted_by_name || (c.merged_into_contact_id ? 'Merge' : '—')}</td>
                                    <td className="px-3 py-2.5 text-sm text-stone-500">{(c.deleted_at || c.merged_at) ? new Date(c.deleted_at || c.merged_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-3 py-2.5">
                                        {c.merged_into_contact_id ? (
                                            <span className="text-xs text-stone-500" title="Undo the merge from Admin › Duplicate contacts › Recent merges">Undo the merge to restore</span>
                                        ) : (
                                            <button onClick={() => handleRestore([c.contact_id])}
                                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                                                <RotateCcw className="w-3 h-3" /> Restore
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <TablePagination variant="split" alwaysShow page={deletedPage} totalPages={deletedPaging.totalPages(deletedTotal)}
                        total={deletedTotal} pageSize={deletedPageSize} onPageChange={deletedPaging.setPage}
                        onPageSizeChange={deletedPaging.setPageSize} />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Delete {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-stone-600">
                            This will soft-delete the selected contact{selectedIds.size > 1 ? 's' : ''}. They can be restored from the "Deleted" tab.
                        </p>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                Type DELETE to confirm
                            </label>
                            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE" autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkDelete} disabled={deleteConfirmText !== 'DELETE' || deleting}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Tag Modal */}
            <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Tag className="w-5 h-5" />
                            Tag {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Action</label>
                            <select value={bulkTagAction} onChange={e => setBulkTagAction(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="add">Add Tags</option>
                                <option value="remove">Remove Tags</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags</label>
                            <TagInput
                                value={bulkTags}
                                onChange={setBulkTags}
                                placeholder={bulkTagAction === 'add' ? 'Add tags...' : 'Tags to remove...'}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkTagModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkTag} disabled={!bulkTags.length || bulkTagging}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {bulkTagging ? 'Updating...' : `${bulkTagAction === 'add' ? 'Add' : 'Remove'} Tags`}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* BUG-040 — Bulk Enroll Modal */}
            <Dialog open={showBulkEnrollModal} onOpenChange={setShowBulkEnrollModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Send className="w-5 h-5" />
                            Enroll {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''} in a Campaign
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Campaign</label>
                            <select value={selectedCampaignId} onChange={e => setSelectedCampaignId(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="">— Select a campaign —</option>
                                {campaignsList.map(c => (
                                    <option key={c.campaign_id} value={c.campaign_id}>
                                        {c.name} {c.status && c.status !== 'active' ? `[${c.status}]` : ''} · {(c.steps || []).length} steps
                                    </option>
                                ))}
                            </select>
                            {campaignsList.length === 0 && (
                                <p className="text-xs text-stone-400 mt-1">No campaigns found.</p>
                            )}
                        </div>
                        <p className="text-xs text-stone-500">
                            Contacts already enrolled (active or paused) or missing required data will be skipped.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowBulkEnrollModal(false)}
                                className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                            <button onClick={handleBulkEnroll} disabled={!selectedCampaignId || enrolling}
                                className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                {enrolling ? 'Enrolling...' : 'Enroll'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Existing-contact event enrollment (#893) */}
            <Dialog open={showEventEnrollModal} onOpenChange={setShowEventEnrollModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <CalendarDays className="w-5 h-5" /> Add contacts to an event
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-xs text-stone-500">Only visible existing contacts are enrolled. Existing owners and attribution are preserved.</p>
                        <select value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm">
                            <option value="">— Select an event —</option>
                            {eventOptions.map(event => <option key={event.event_id} value={event.event_id}>{event.name || event.title}</option>)}
                        </select>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowEventEnrollModal(false)} className="rounded-lg border border-stone-200 px-4 py-2 text-sm text-stone-700">Cancel</button>
                            <button onClick={handleEventEnroll} disabled={!selectedEventId || eventEnrolling} className="rounded-lg bg-stone-900 px-4 py-2 text-sm text-white disabled:opacity-50">
                                {eventEnrolling ? 'Adding…' : 'Add to event'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>


            {/* #863 — Contact Owner reassignment, requests and Undo */}
            {showReassign && (
                <ReassignOwnerDialog
                    open
                    onClose={() => setShowReassign(false)}
                    contactIds={Array.from(selectedIds)}
                    labelFor={contactLabel}
                    owners={teamMembers}
                    getAuthHeader={getAuthHeader}
                    canRestore={canRestoreOwner}
                    onDone={handleReassignDone}
                    onUndo={(operationId) => setUndoOperationId(operationId)}
                    onSelectionChanged={() => setShowReassign(false)}
                />
            )}
            {showOwnerRequests && (canApproveOwnerChanges || canRestoreOwner) && (
                <OwnerRequestsDialog
                    open
                    onClose={closeOwnerRequests}
                    getAuthHeader={getAuthHeader}
                    currentUserId={user?.user_id}
                    canApprove={canApproveOwnerChanges}
                    canRestore={canRestoreOwner}
                    onChanged={(opts) => { reloadContacts(opts); loadOwnerRequestsTotal(); }}
                />
            )}
            <UndoOwnerChangeDialog
                open={!!undoOperationId}
                operationId={undoOperationId}
                getAuthHeader={getAuthHeader}
                onClose={() => setUndoOperationId(null)}
                onDone={(_res, opts) => reloadContacts(opts)}
            />

            {/* Contact Modal */}
            <ContactModal
                open={showModal}
                onClose={() => { setShowModal(false); setEditContact(null); }}
                contact={editContact}
                onSave={handleSave}
            />
        </div>
    );
}
